import { gql } from "@apollo/client";

// GraphQL Queries
export const GET_ALARM_DATASOURCE_BY_UUID = gql`
  query GetAlarmDatasourceByUUID($sourceUUID: uuid!) {
    source: get_datasource_by_uuid(args: { source_uuid: $sourceUUID }) {
      source_uuid
      source_id
      unit {
        id
        name
        symbol
      }
      name
      system_name
      type
    }
  }
`;

export const GET_ALARM_BY_SOURCE_UUID = gql`
  query GetAlarmBySourceUUID($sourceUUID: uuid!) {
    alert(where: { source_uuid: { _eq: $sourceUUID } }) {
      id
      lower_limit
      upper_limit
      channel_id
      formula_id
      active
      internal
      level
      alert_frequency_minutes
      alert_timeframe_minutes
      alert_recipients {
        id
        alert_id
        email_recipient
        sms_recipient
      }
      alert_options {
        id
        alert_id
        ignore_spikes
        trend_alarm
        trend_alarm_interval
        trend_alarm_interval_unit
        change_alarm
        change_alarm_threshold
        change_alarm_comparison
        change_alarm_interval
        change_alarm_interval_unit
      }
    }
  }
`;

export const GET_STATION_NO_DATA_ALARMS = gql`
  query GetStationNoDataAlarms($stationId: Int!) {
    alert(
      where: {
        station_id: { _eq: $stationId }
        kind: { _eq: "station_no_data" }
      }
    ) {
      id
      kind
      station_id
      no_data_minutes
      triggered_at
      active
      internal
      level
      alert_frequency_minutes
      alert_recipients {
        id
        alert_id
        email_recipient
        sms_recipient
      }
    }
  }
`;

export const GET_ALL_STATIONS_FOR_ALARMS = gql`
  query GetAllStationsForAlarms {
    station(order_by: { name: asc }) {
      id
      identifier
      name
      origin
      status
      fmisid
    }
  }
`;

// Stations that already have a (non-internal) station not sending data alarm; only one is allowed per station
export const GET_STATIONS_WITH_NO_DATA_ALARMS = gql`
  query GetStationsWithNoDataAlarms {
    alert(
      where: { kind: { _eq: "station_no_data" }, internal: { _eq: false } }
    ) {
      id
      station_id
    }
  }
`;

export const GET_STATION_DATASOURCES_FOR_ALARMS = gql`
  query GetStationDataSourcesForAlarms($stationId: Int!) {
    station(where: { id: { _eq: $stationId } }) {
      id
      name
      datasources(where: { visible: { _eq: true } }, order_by: { order: asc }) {
        type
        source_id
        source_uuid
        name
        order
        visible
        unit {
          id
          symbol
        }
      }
    }
  }
`;

// Constants
export const INTERVAL_UNITS = ["minutes", "hours", "days"];

// Alarm kinds (alert.kind)
export const VALUE_ALARM_KIND = "value";
export const STATION_NO_DATA_ALARM_KIND = "station_no_data";

// Alarm history types written by the station not sending data check
export const NO_DATA_ALARM_TYPE = "no_data";
export const NO_DATA_RECOVERED_ALARM_TYPE = "no_data_recovered";

// Must match NO_DATA_MINUTES_CHOICES in mg-core/mg/core/services/alerts.py
export const DEFAULT_NO_DATA_PERIODS: Array<[string, number]> = [
  ["1 h", 60],
  ["6 h", 6 * 60],
  ["12 h", 12 * 60],
  ["24 h", 24 * 60],
  ["48 h", 2 * 24 * 60],
  ["3 d", 3 * 24 * 60],
  ["7 d", 7 * 24 * 60],
];
export const DEFAULT_NO_DATA_MINUTES = 24 * 60;

export const isStationNoDataAlarm = (
  alarm: { kind?: string | null } | null | undefined
): boolean => alarm?.kind === STATION_NO_DATA_ALARM_KIND;

// Stations that a station not sending data alarm can be set for (see create_station_no_data_alert)
export const isNoDataAlarmEligibleStation = (station: {
  fmisid?: string | null;
  status?: string | null;
}): boolean => !station.fmisid && station.status === "active";

export const ALARM_LEVELS = ["notice", "warning", "alert"];

export const ALARM_LEVEL_COLORS: Record<string, string> = {
  notice: "rgba(255, 193, 7, 1)", // Yellow
  warning: "rgba(255, 152, 0, 1)", // Orange
  alert: "rgba(244, 67, 54, 1)", // Red
};

export const ALARM_LEVEL_AREA_COLORS: Record<string, string> = {
  notice: "rgba(255, 193, 7, 0.15)",
  warning: "rgba(255, 152, 0, 0.3)",
  alert: "rgba(244, 67, 54, 0.15)",
};

export const DEFAULT_ALARM_FREQUENCIES: Array<[string, number]> = [
  ["15 m", 15],
  ["30 m", 30],
  ["1 h", 60],
  ["6 h", 6 * 60],
  ["24 h", 24 * 60],
  ["3 d", 3 * 24 * 60],
  ["7 d", 7 * 24 * 60],
];

export const DEFAULT_ALARM_TIMEFRAMES: Array<[string, number]> = [
  ["15 m", 15],
  ["30 m", 30],
  ["1 h", 60],
  ["2 h", 2 * 60],
];

// Legacy exports for backward compatibility
// These allow old code using ALERT_* naming to continue working
export const ALERT_LEVELS = ALARM_LEVELS;
export const ALERT_LEVEL_COLORS = ALARM_LEVEL_COLORS;
export const ALERT_LEVEL_AREA_COLORS = ALARM_LEVEL_AREA_COLORS;

// Utility function for parsing float values
export function parseFloatOrEmpty(value: string): number | string {
  if (!value) {
    return "";
  }
  const parsed = parseFloat(value);
  if (isNaN(parsed)) {
    return "";
  }
  return parsed;
}

// Utility function for formatting alarm frequency display
export function formatAlarmFrequency(
  minutes: number | null | undefined
): string {
  if (!minutes) return "N/A";

  if (minutes < 60) {
    return `${minutes} m`;
  } else if (minutes < 1440) {
    return `${minutes / 60} h`;
  } else {
    return `${minutes / 1440} d`;
  }
}

// Uses the same labels as the no data period selector
export function formatNoDataPeriod(minutes: number | null | undefined): string {
  const preset = DEFAULT_NO_DATA_PERIODS.find(([, value]) => value === minutes);
  return preset ? preset[0] : formatAlarmFrequency(minutes);
}

// Utility function for formatting limit values
export function formatLimit(limit: number | null | undefined): string {
  if (limit === null || limit === undefined) {
    return "N/A";
  }
  return limit.toString();
}
